import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {Store} from "@ngrx/store";
import {skip, switchMap, take} from "rxjs";
import {ASSET_MANAGEMENT_URL} from "../../../app.config";
import {API_ENDPOINTS, APP_CONSTANTS} from "../../../app.constants";
import {ToastSeverityEnum} from "../../../enums";
import {prepareFiltersForParams} from "../../../helpers";
import {consolidateUsers, DtoUserResultsInterface, FilterInterface, UserModel} from "../../../interfaces";
import {mapUserFromDtoArray, mapUserToDto} from "../../../mapping";
import {
  addUser,
  addUsers,
  setConsolidatedUsers,
  setPaginatedUsers,
  setUsers,
  setUsersPaginationInfo,
  updateUser
} from "../../../store/actions/users.actions";
import {selectUserFilters, selectUserItems} from "../../../store/reducers/users.reducer";
import {ToastsService} from "../../app-services";

const pageSize = APP_CONSTANTS.PAGINATION_PAGE_SIZE;

@Injectable({
  providedIn: "root"
})
export class UsersService {
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly toastsService = inject(ToastsService);
  private readonly assetManagementUrl = inject(ASSET_MANAGEMENT_URL);

  constructor() {
    this.store
      .select(selectUserFilters)
      .pipe(skip(1))
      .subscribe(() => {
        this.getUsersPaginated();
      });
  }

  public getUsersPaginated(): void {
    this.store.dispatch(setUsers({users: []}));
    this.getUsersRecursive(0);
  }

  private getUsersRecursive(offset: number): void {
    this.store
      .select(selectUserFilters)
      .pipe(
        switchMap((filters: FilterInterface) => {
          const recursiveFilters = {...filters, limit: APP_CONSTANTS.MAX_API_RESULTS_LIMIT, offset};
          return this.httpClient.get<DtoUserResultsInterface>(this.assetManagementUrl + API_ENDPOINTS.USERS, {
            params: prepareFiltersForParams(recursiveFilters)
          });
        }),
        take(1)
      )
      .subscribe({
        next: data => {
          const users = mapUserFromDtoArray(data.results ?? []);

          this.store.dispatch(addUsers({users}));

          // Recursive check
          if (data.results?.length === APP_CONSTANTS.MAX_API_RESULTS_LIMIT) {
            setTimeout(() => {
              this.getUsersRecursive(offset + APP_CONSTANTS.MAX_API_RESULTS_LIMIT);
            }, 100);
          } else {
            this.finalizeUserConsolidation();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: "Error fetching Users Paginated, Status " + error.status
          });
        }
      });
  }

  private finalizeUserConsolidation(): void {
    this.store
      .select(selectUserItems)
      .pipe(take(1))
      .subscribe(allUsers => {
        const consolidatedUsersList = consolidateUsers(allUsers);

        this.store.dispatch(setPaginatedUsers({users: allUsers}));
        this.store.dispatch(setConsolidatedUsers({users: consolidatedUsersList}));

        this.store
          .select(selectUserFilters)
          .pipe(take(1))
          .subscribe(currentFilters => {
            const startIndex = currentFilters.offset || 0;

            this.store.dispatch(
              setUsersPaginationInfo({
                paginationInfo: {
                  from: startIndex + 1,
                  to: Math.min(startIndex + pageSize, consolidatedUsersList.length),
                  totalResults: consolidatedUsersList.length,
                  currentPage: Math.floor(startIndex / pageSize) + 1,
                  totalPages: Math.ceil(consolidatedUsersList.length / pageSize)
                }
              })
            );
          });
      });
  }

  public createUser(model: UserModel): void {
    this.httpClient
      .put(this.assetManagementUrl + API_ENDPOINTS.USERS, mapUserToDto(model), {params: {userId: model.userId}})
      .subscribe({
        complete: () => {
          this.store.dispatch(addUser({user: model}));
          this.getUsersPaginated();
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Success,
            message: `Created new User ${model.userId}`
          });
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to create new User ${model.userId}, Status ${error.status}`
          });
        }
      });
  }

  public createUserWithPermissions(userId: string, userPermissions: Array<UserModel>): void {
    const userIdEncoded = encodeURIComponent(userId);
    const requestBody = userPermissions.map(user => mapUserToDto(user));

    this.httpClient.put(this.assetManagementUrl + API_ENDPOINTS.USERS + `/${userIdEncoded}`, requestBody).subscribe({
      complete: () => {
        this.store.dispatch(addUsers({users: userPermissions}));
        this.getUsersPaginated();
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Success,
          message: `Created new User ${userId} with ${userPermissions.length} permission${userPermissions.length > 1 ? "s" : ""}`
        });
      },
      error: (error: HttpErrorResponse) => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to create new User ${userId}, Status ${error.status}`
        });
      }
    });
  }

  public updateUser(model: UserModel): void {
    this.httpClient
      .post(this.assetManagementUrl + API_ENDPOINTS.USERS, mapUserToDto(model), {
        params: {userId: model.userId}
      })
      .subscribe({
        complete: () => {
          this.store.dispatch(updateUser({user: model}));
          this.getUsersPaginated();
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Success,
            message: `Updated User ${model.userId}`
          });
        },
        error: (error: HttpErrorResponse) => {
          this.toastsService.addToast({
            severity: ToastSeverityEnum.Error,
            message: `Failed to update User ${model.userId}, Status ${error.status}`
          });
        }
      });
  }

  public deleteUser(model: UserModel): void {
    const userIdEncoded = encodeURIComponent(model.userId);
    this.httpClient.delete(this.assetManagementUrl + API_ENDPOINTS.USERS + `/${userIdEncoded}/${model.id}`).subscribe({
      complete: () => {
        this.getUsersPaginated();
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Success,
          message: `Deleted User ${model.userId}`
        });
      },
      error: (error: HttpErrorResponse) => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to delete User ${model.userId}, Status ${error.status}`
        });
      }
    });
  }

  public deleteEntireUser(userId: string): void {
    const userIdEncoded = encodeURIComponent(userId);
    this.httpClient.delete(this.assetManagementUrl + API_ENDPOINTS.USERS + `/${userIdEncoded}`).subscribe({
      complete: () => {
        this.getUsersPaginated();
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Success,
          message: `Deleted all access for User ${userId}`
        });
      },
      error: (error: HttpErrorResponse) => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to delete User ${userId}, Status ${error.status}`
        });
      }
    });
  }

  public updateUserAccess(userId: string, userAccess: Array<UserModel>): void {
    const userIdEncoded = encodeURIComponent(userId);
    const requestBody = userAccess.map(user => mapUserToDto(user));

    this.httpClient.put(this.assetManagementUrl + API_ENDPOINTS.USERS + `/${userIdEncoded}`, requestBody).subscribe({
      complete: () => {
        this.getUsersPaginated();
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Success,
          message: `Updated access for User ${userId}`
        });
      },
      error: (error: HttpErrorResponse) => {
        this.toastsService.addToast({
          severity: ToastSeverityEnum.Error,
          message: `Failed to update User ${userId}, Status ${error.status}`
        });
      }
    });
  }
}
